// react
import { useEffect, useCallback, useRef } from 'react';

// api
import store, { dispatch, useSelector } from 'store';
import { getChatToken, setChatToken } from 'store/slices/chatSlice';
import { getAccount } from 'store/slices/iamSlice';
import { useGenerateChatTokenMutation } from 'store/api/chat';

// hooks
import { useAuth } from 'hooks';
import { useFeatureFlags } from './useFeatureFlags';

// context
import { AccountRole } from 'context';

// intercom
import { Intercom } from '@intercom/messenger-js-sdk';
import { Intercom as CapacitorIntercom } from '@capacitor-community/intercom';
import { Capacitor } from '@capacitor/core';

// helpers
import { sleep } from '@karehero/helpers/timers';

// observability
import * as Sentry from '@sentry/react';

const INTERCOM_APP_ID = import.meta.env.REACT_APP_INTERCOM_APP_ID || '';
console.log('[useIntercom] INTERCOM_APP_ID', INTERCOM_APP_ID);

const TOKEN_REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000; // daily
const MAX_REFRESH_ATTEMPTS = 5;
const BASE_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 60_000;

// helpers
// Reads the store directly: the async refresh loop outlives the render that
// started it, so a value captured from a hook would be stale after sign-out.
const isSignedIn = () => !!getAccount(store.getState());

export const useIntercom = () => {
  // state
  const account = useSelector(getAccount);
  const chatToken = useSelector(getChatToken);
  const isIntercomReady = useRef(false);
  const isLoading = useRef(false);
  const refreshInFlight = useRef<Promise<void> | null>(null);
  const refreshTimeoutId = useRef<number | null>(null);
  const isMountedRef = useRef(false);
  const chatTokenRef = useRef(chatToken);

  // hooks
  const [generateChatToken] = useGenerateChatTokenMutation();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { flags } = useFeatureFlags();

  useEffect(() => {
    chatTokenRef.current = chatToken;
  }, [chatToken]);

  const retrieveJwtWithBackoff = useCallback(async () => {
    if (refreshInFlight.current) return refreshInFlight.current;

    refreshInFlight.current = (async () => {
      let lastError: unknown = null;

      for (let attempt = 0; attempt < MAX_REFRESH_ATTEMPTS; attempt++) {
        // The user signed out while a previous attempt was backing off
        if (!isSignedIn()) return;

        try {
          const token = await generateChatToken().unwrap();
          if (!isSignedIn()) {
            // The user signed out while this request was in flight. The slice
            // has already stored the response, so clear it again rather than
            // leaving a valid token in localStorage after sign-out.
            dispatch(setChatToken(null));
            return;
          }
          dispatch(setChatToken(token));
          return;
        } catch (err) {
          lastError = err;
          const baseDelay = Math.min(
            MAX_RETRY_DELAY_MS,
            BASE_RETRY_DELAY_MS * Math.pow(2, attempt),
          );
          const jitteredDelay = Math.floor(
            baseDelay * (0.8 + Math.random() * 0.4),
          );
          console.warn(
            `[useIntercom] retrieveJwt failed (attempt ${attempt + 1}/${MAX_REFRESH_ATTEMPTS}); retrying in ${jitteredDelay}ms`,
            err,
          );
          await sleep(jitteredDelay);
        }
      }

      Sentry.captureMessage('intercom.jwt_retrieval_failed', {
        level: 'error',
        extra: { attempts: MAX_REFRESH_ATTEMPTS, lastError },
      });
      console.error('[useIntercom] retrieveJwt failed; giving up', lastError);
    })().finally(() => {
      refreshInFlight.current = null;
    });

    return refreshInFlight.current;
  }, [generateChatToken]);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimeoutId.current != null) {
      window.clearTimeout(refreshTimeoutId.current);
      refreshTimeoutId.current = null;
    }
  }, []);

  const scheduleDailyRefresh = useCallback(() => {
    clearRefreshTimer();
    if (!isMountedRef.current) return;

    refreshTimeoutId.current = window.setTimeout(async () => {
      if (!isMountedRef.current) return;

      await retrieveJwtWithBackoff();
      scheduleDailyRefresh();
    }, TOKEN_REFRESH_INTERVAL_MS);
  }, [clearRefreshTimer, retrieveJwtWithBackoff]);

  const shutdown = useCallback(() => {
    clearRefreshTimer();
    dispatch(setChatToken(null));
    isIntercomReady.current = false;
    isLoading.current = false;
    if (Capacitor.isNativePlatform()) {
      CapacitorIntercom.logout();
    } else {
      window.Intercom?.('shutdown');
    }
  }, []);

  // checks whether intercom has fully loaded yet
  const pollIntercomReady = useCallback(() => {
    if (isIntercomReady.current) return;

    const intervalID = setInterval(() => {
      if (isIntercomReady.current) {
        clearInterval(intervalID);
        return;
      }

      if (
        window.Intercom &&
        !window.Intercom.toString().includes('queueHolder.loaderQueue')
      ) {
        clearInterval(intervalID);
        isLoading.current = false;
        isIntercomReady.current = true;
      }
    }, 50);
  }, []);

  const mountCapacitorIntercom = useCallback(async () => {
    const token = chatTokenRef.current;
    if (!token || !token.userID || !token.email) {
      Sentry.addBreadcrumb({
        category: 'intercom',
        message: 'mountCapacitorIntercom skipped: missing chat token',
        level: 'warning',
      });
      return;
    }

    try {
      Sentry.addBreadcrumb({
        category: 'intercom',
        message: 'setting user jwt',
        level: 'info',
      });
      await CapacitorIntercom.setUserJwt({ jwt: token?.token });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { component: 'intercom', operation: 'setUserJwt' },
      });
      throw err;
    }

    try {
      Sentry.addBreadcrumb({
        category: 'intercom',
        message: 'registering identified user',
        data: { userId: token?.userID, email: token?.email },
        level: 'info',
      });
      await CapacitorIntercom.registerIdentifiedUser({
        userId: token?.userID,
        email: token?.email,
      });
    } catch (err) {
      Sentry.captureException(err, {
        tags: { component: 'intercom', operation: 'registerIdentifiedUser' },
      });
      throw err;
    }
  }, []);

  // mounts intercom and starts polling for whether it has mounted
  const mountIntercom = useCallback(async () => {
    if (isIntercomReady.current || isLoading.current) return;
    isLoading.current = true;

    // On native, if we don't have a token yet, fetch one before proceeding.
    // This avoids a race where mountIntercom is called before the JWT arrives.
    if (Capacitor.isNativePlatform() && !chatTokenRef.current?.token) {
      await retrieveJwtWithBackoff();
    }

    const token = chatTokenRef.current;
    if (!token) {
      isLoading.current = false;
      return;
    }

    if (Capacitor.isNativePlatform()) {
      Sentry.addBreadcrumb({
        category: 'intercom',
        message: 'mounting capacitor intercom',
        level: 'info',
      });
      try {
        await mountCapacitorIntercom();
        isIntercomReady.current = true;
      } finally {
        isLoading.current = false;
      }
      return;
    }

    Intercom({
      app_id: INTERCOM_APP_ID,
      region: 'eu',
      user_id: token?.userID,
      name: token?.name,
      email: token?.email,
      created_at: token?.createdAt,
      intercom_user_jwt: token?.token,
      session_duration: 86400000,
      hide_default_launcher: true,
    });

    pollIntercomReady();

    return;
  }, [mountCapacitorIntercom, pollIntercomReady, retrieveJwtWithBackoff]);

  const showIntercom = useCallback(async (): Promise<void> => {
    // Best-effort: if we don't have a token yet, fetch one before showing.
    if (!chatTokenRef.current?.token) {
      await retrieveJwtWithBackoff();
    }

    // If we don't have a token yet, ensure Intercom is mounted before showing.
    await mountIntercom();

    if (Capacitor.isNativePlatform()) {
      CapacitorIntercom.displayMessenger();
    } else {
      window.Intercom?.('show');
    }
  }, [mountIntercom, retrieveJwtWithBackoff]);

  // effects
  // checks whether to show intercom UI once it's loaded
  useEffect(() => {
    if (isLoading.current || !isIntercomReady.current) return;

    const currentPath = window.location.pathname;
    const messengerParam = new URLSearchParams(window.location.search).get(
      'messenger',
    );
    if (currentPath === '/messenger' || messengerParam === 'true') {
      showIntercom();
    }
  }, [showIntercom]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearRefreshTimer();
    };
  }, [clearRefreshTimer]);

  useEffect(() => {
    if (
      isAuthenticated &&
      !isAuthLoading &&
      account &&
      account.id &&
      account.accountRole?.name === AccountRole.MEMBER &&
      flags?.intercomChat
    ) {
      retrieveJwtWithBackoff();
      scheduleDailyRefresh();
    } else {
      clearRefreshTimer();
    }
  }, [
    isAuthenticated,
    isAuthLoading,
    account,
    flags?.intercomChat,
    clearRefreshTimer,
    retrieveJwtWithBackoff,
    scheduleDailyRefresh,
  ]);

  // If the app was backgrounded (esp. native), refresh on wake and reset the timer.
  useEffect(() => {
    const isEligible =
      isAuthenticated &&
      !isAuthLoading &&
      account?.id &&
      account.accountRole?.name === AccountRole.MEMBER &&
      flags?.intercomChat;

    if (!isEligible) return;

    const onWake = async () => {
      if (!isMountedRef.current) return;
      await retrieveJwtWithBackoff();
      scheduleDailyRefresh();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') onWake();
    };

    window.addEventListener('focus', onWake);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', onWake);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [
    account,
    flags?.intercomChat,
    isAuthenticated,
    isAuthLoading,
    retrieveJwtWithBackoff,
    scheduleDailyRefresh,
  ]);

  return {
    showIntercom,
    mountIntercom,
    shutdown,
    isLoading: isLoading.current,
    isIntercomReady: isIntercomReady.current,
  };
};

export default useIntercom;
